import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, filter, Observable, Subscription, take } from 'rxjs';
import { BaseService } from 'src/app/common/base/base.service';
import { ActionPendingCatalog, Roles, StatusPendingCatalog } from 'src/app/common/enumerations/enumerations';
import { PendingApprovals, PendingCatalog, PendingCatalogType } from 'src/app/services/pending-approvals/pending-approval';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { ChecklistPriv, ChecklistTemplate, ChecklistTemplateStatusEnum, TemplateType } from '../checklists/checklists';
import { ProcedureSignatureType } from '../procedure/enums/procedure-signature-type.enum';
import { utils } from 'src/app/modules/libs/utils';
import { APLGroup } from '../apl/services/apl-group.model';
import { APLCategory } from '../apl/services/apl-category.model';

@Injectable({
  providedIn: 'root'
})
export class PendingApprovalsV2Service extends BaseService<PendingApprovals, number> {

  public pendingApprovalsSubject!: BehaviorSubject<PendingApprovals | null>;

  pendingApprovals!: PendingApprovals;
  myPendingChanges!: PendingApprovals;

  pendingApprovals$!: Observable<PendingApprovals>;
  pendingApprovalsSubs!: Subscription;

  api = '/PendingApproval';

  checklistTemplates?: ChecklistTemplate[];
  checklistTemplates$!: Observable<ChecklistTemplate[]>;
  checklistTemplatesSubs!: Subscription;

  templateTypes?: TemplateType[];
  templateTypes$!: Observable<TemplateType[]>;
  templateTypesSubs!: Subscription;

  aplGroup?: APLGroup;
  aplGroups?: APLGroup[];
  aplGroupsSubs!: Subscription;

  aplCategory?: APLCategory;
  aplCategories?: APLCategory[];
  aplCategoriesSubs!: Subscription;

  constructor(
    protected override injector: Injector,
  ) {
    super(injector, '/PendingApproval');
    this.pendingApprovalsSubject = new BehaviorSubject<PendingApprovals | null>(null);
    this.loadChecklistTemplates();
    this.loadAPLGroups();
    this.loadAPLCategories();
  }

  override currentUserUpdated(): void {
    this.loadChecklistTemplates();
    this.loadAPLGroups();
  }

  get(): Observable<PendingApprovals> {
    return this.http.get<PendingApprovals>(this.URL);
  }

  loadChecklistTemplates() {
    this.checklistTemplates$ = this.store.select(state => state.ChecklistTemplates.pending);
    this.checklistTemplatesSubs = this.checklistTemplates$.pipe(
      // only when data actually has items
      filter(data => !!data && data.length > 0),
      // only the first such emission, then complete
      take(1)
    ).subscribe(data => {
      this.checklistTemplates = data;
      this.loadTemplateTypes();
    });
  }

  loadTemplateTypes() {
    this.templateTypes$ = this.store.select(state => state.TemplateTypes.data);

    this.templateTypesSubs = this.templateTypes$
      .pipe(
        // only when data actually has items
        filter(data => !!data && data.length > 0),
        // only the first such emission, then complete
        take(1)
      )
      .subscribe(data => {
        this.templateTypes = data;
        this.loadPendingApprovals();
      });
  }


  loadAPLGroups(loadApprovals: boolean = true) {
    this.aplGroupsSubs = this.store.select(state => state.APLGroup.all).pipe(
      // only when data actually has items
      filter(data => !!data && data.length > 0),
      // only the first such emission, then complete
      take(1)
    ).subscribe(data => {
      if (data?.length) {
        this.aplGroups = data;
        if (loadApprovals)
          this.loadPendingApprovals();
      }
    });
  }

  loadAPLCategories(loadApprovals: boolean = true) {
    this.aplCategoriesSubs = this.store.select(state => state.APLCategory.APLCategories).pipe(
      // only when data actually has items
      filter(data => !!data && data.length > 0),
      // only the first such emission, then complete
      take(1)
    ).subscribe(data => {
      if (data?.length) {
        this.aplCategories = data;
        if (loadApprovals)
          this.loadPendingApprovals();
      }
    });
  }

  loadPendingApprovals() {
    this.pendingApprovals$ = this.store.select(state => state.PendingApprovals.data);
    this.pendingApprovalsSubs = this.pendingApprovals$.pipe(
      // only when data actually has items
      filter(data => !!data),
      // only the first such emission, then complete
      take(1)
    ).subscribe(data => {
      if (data && this.currentUser) {
        const p: PendingApprovals = {
          pendingAccelerators: this.getMyApprovals(data.pendingAccelerators ?? []),
          pendingUsers: this.getMyApprovals(data.pendingUsers),
          pendingRoles: this.getMyApprovals(data.pendingRoles),
          pendingNotificationTemplates: this.getMyApprovals(data.pendingNotificationTemplates),
          pendingResources: this.getMyApprovals(data.pendingResources),
          pendingRSSDb: this.getMyApprovals(data.pendingRSSDb),
          pendingChecklistTemplates: this.getMyApprovals(data.pendingChecklistTemplates),
          pendingAPLCategories: this.getMyApprovals(data.pendingAPLCategories),
          pendingAPLGroups: this.getMyApprovals(data.pendingAPLGroups)
        };
        p.total =
          (p.pendingAccelerators?.length ?? 0) +
          (p.pendingUsers?.length ?? 0) +
          (p.pendingRoles?.length ?? 0) +
          (p.pendingResources?.length ?? 0) +
          (p.pendingRSSDb?.length ?? 0) +
          (p.pendingChecklistTemplates?.length ?? 0) +
          (p.pendingAPLCategories.length ?? 0) +
          (p.pendingAPLGroups.length ?? 0);
        this.pendingApprovals = p;

        const q: PendingApprovals = {
          pendingAccelerators: this.getMyChanges(data.pendingAccelerators),
          pendingUsers: this.getMyChanges(data.pendingUsers),
          pendingRoles: this.getMyChanges(data.pendingRoles),
          pendingNotificationTemplates: this.getMyChanges(data.pendingNotificationTemplates),
          pendingResources: this.getMyChanges(data.pendingResources),
          pendingRSSDb: this.getMyChanges(data.pendingRSSDb),
          pendingChecklistTemplates: this.getMyChanges(data.pendingChecklistTemplates),
          pendingAPLCategories: this.getMyChanges(data.pendingAPLCategories),
          pendingAPLGroups: this.getMyChanges(data.pendingAPLGroups)
        };
        q.total =
          (q.pendingAccelerators?.length ?? 0) +
          (q.pendingUsers?.length ?? 0) +
          (q.pendingRoles?.length ?? 0) +
          (q.pendingResources?.length ?? 0) +
          (q.pendingRSSDb?.length ?? 0) +
          (q.pendingChecklistTemplates?.length ?? 0) +
          (q.pendingAPLCategories.length ?? 0) +
          (q.pendingAPLGroups.length ?? 0);
        this.myPendingChanges = q;
        this.pendingApprovalsSubject.next(data);
      }
    });
  }

  private setNames(data: PendingCatalog[]) {
    data?.map(x => {
      x.actionName = ActionPendingCatalog[x.action];
      if (x.approvalType != PendingCatalogType.ChecklistTemplate)
        x.statusName = StatusPendingCatalog[x.status];
    });
    return data;
  }

  private getMyChanges(data: PendingCatalog[] = []) {
    return this.setNames(data?.filter(x => x.createdBy == this.currentUser?.id && x.show && (x.approvalType != PendingCatalogType.ChecklistTemplate && x.statusBy != this.currentUser.id || x.approvalType == PendingCatalogType.ChecklistTemplate)));
  }

  private getMyApprovals(data: PendingCatalog[] = []) {
    return this.setNames(data?.filter(x => {
      if (x.createdBy == this.currentUser?.id) return false;

      switch (x.approvalType) {
        case PendingCatalogType.Accelerator:
          return x.status == StatusPendingCatalog.Pending && this.hasRoles([Roles.RP]);

        case PendingCatalogType.Beamline:
          return x.status == StatusPendingCatalog.Pending && (this.isSA() && x.pendingID && x.hasResourceChanges && !x.approvedBy || this.hasRoles([Roles.RP]) && x.pendingID && x.hasShieldingChanges && !x.shieldingApprovedBy || !x.pendingID && this.hasPrivilege(PrivilegeEnum.BeamlineRestrictionsApprove));

        case PendingCatalogType.Role:
        case PendingCatalogType.User:
          return x.status == StatusPendingCatalog.Pending && (this.isSA() && x.pendingID || !x.pendingID && this.hasPrivilege(PrivilegeEnum.BeamlineRestrictionsApprove) || this.isAPLManager(x));

        case PendingCatalogType.RSSDb:
          return x.status == StatusPendingCatalog.Pending && (this.isSA() || this.hasPrivilege(PrivilegeEnum.RSSTestDBAuth));

        case PendingCatalogType.KET:
          return this.hasRoles([Roles.SA]);

        case PendingCatalogType.ABSI:
          return this.hasPrivilege(PrivilegeEnum.ApproveABSITemplate);

        case PendingCatalogType.ChecklistTemplate:
          return this.canApproveTemplate(x);

        case PendingCatalogType.APLCategories:
          return this.hasPrivilege(PrivilegeEnum.APLCategoryApproval) || this.canApproveAPLCategory(x);

        case PendingCatalogType.APLGroups:
          return this.hasPrivilege(PrivilegeEnum.APLGroupApproval) || this.canApproveAPLGroup(x);

        default:
          return false;
      }
    })
    );
  }

  canApproveTemplate(templateApproval: PendingCatalog) {
    const checklistTemplate = this.checklistTemplates?.find(t => t.id == templateApproval.pendingID);
    if (checklistTemplate) {
      const templateType = this.templateTypes?.find(t => t.id == checklistTemplate?.templateTypeID);
      checklistTemplate.templateType = templateType;

      switch (templateApproval?.status) {

        case ChecklistTemplateStatusEnum.PendingRev:
          if (checklistTemplate.procedure) {
            return checklistTemplate.procedure.procedureSignatures?.filter(s => s.signatureType == ProcedureSignatureType.Reviewer).map(s => s.userID).includes(this.currentUser?.id ?? 0);
          }
          return false;

        case ChecklistTemplateStatusEnum.CorrectionsPending:
          if (checklistTemplate.procedure) {
            return checklistTemplate.procedure.procedureSignatures?.filter(s => s.signatureType == ProcedureSignatureType.Preparer).map(s => s.userID).includes(this.currentUser?.id ?? 0);
          }
          return false;

        case ChecklistTemplateStatusEnum.PendingApr:
          if (checklistTemplate.procedure) {
            return checklistTemplate.procedure.procedureSignatures?.filter(s => s.signatureType == ProcedureSignatureType.Approver).map(s => s.userID).includes(this.currentUser?.id ?? 0);
          }
          else {
            return utils.intersect(checklistTemplate?.templateType?.documentType?.templateRolePermissions?.filter(p => p.checklistPrivilege == ChecklistPriv.Approve)?.map(p => p.roleID), this.currentUser?.userRole?.map(r => r.roleID));
          }

        default:
          return false;

      }
    }
    return false;
  }

  canApproveAPLGroup(data: PendingCatalog) {
    if (!this.aplGroups) {
      this.loadAPLGroups();
      return;
    }
    else if (!this.aplCategories) {
      this.loadAPLCategories();
      return;
    }
    else {
      const aplGroup = this.aplGroups?.find(ag => ag.id == data.id || ag.id == data.pendingID);
      const aplCategory = this.aplCategories?.find(ac => ac.aplCategoryMasterID == aplGroup?.aplGroupMaster?.aplCategoryMasterID && ac.status == 1);

      return aplGroup?.aplGroupManagers?.map(gm => gm.userID).includes(this.currentUser?.id) || aplCategory?.aplCategoryManagers.map(ac => ac.userID).includes(this.currentUser?.id);
    }
  }

  canApproveAPLCategory(data: PendingCatalog) {
    if (!this.aplCategories) {
      this.loadAPLCategories();
      return;
    }
    else {
      const aplCategory = this.aplCategories?.find(ag => ag.id == data.id || ag.id == data.pendingID);
      return aplCategory?.aplCategoryManagers.map(cm => cm.userID).includes(this.currentUser?.id);
    }
  }

  isAPLManager(x: PendingCatalog) {
    if (!this.aplGroups) {
      this.loadAPLGroups();
      return;
    }
    else if (!this.aplCategories) {
      this.loadAPLCategories();
      return;
    }
    if (x.approvalType == PendingCatalogType.Role && this.aplCategories?.length && this.aplGroups?.length) {
      const aplGroups = this.aplGroups.filter(g => g.aplGroupRoles?.some(gr => gr.roleID == x.id));
      const aplCategories = this.aplCategories?.filter(ac => aplGroups?.map(ag => ag.aplGroupMaster?.aplCategoryMasterID).includes(ac.aplCategoryMasterID) && ac.status == 1);
      return aplGroups.some(g => g.aplGroupManagers?.find(m => m.userID == this.currentUser?.id)) ||
        aplCategories?.some(c => c.aplCategoryManagers.some(m => m.userID == this.currentUser?.id));
    }
    return false;
  }
}
